// LamplightTeachBack — Tier-A "Lamplight" skin for teach-back.
//
// The scene (Dylan's pick: Desk + Filament): a mentor and a less-experienced
// colleague at a real desk under a real lamp, working through the colleague's
// draft with a red pen. Register-NEUTRAL / professional-default (§9.2) — the
// ritual is adult (editors, senior reviewers, managers). The document sits in a
// pool of lamplight on a wood desk; a red felt pen rests beside it; the coach's
// notes are scrawled in the margin.
//
// THE JUICE (§8.12b — visualise cognition, not clicks): the LAMP'S FILAMENT is
// the other person's understanding. It sits dim and thin while they're stuck and
// blazes bright + floods the desk with warm light when the teaching lands. The
// pool of light IS the understanding meter — always visible, top of frame.
//
// Shares the EXACT machine + capture with the standard renderer via useTeachBack
// (a flagship can't drift — the audit's §8.1 lesson).

import { useEffect } from 'react';
import type { TeachBackSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { useTeachBack } from './useTeachBack';

const STYLE_ID = 'lamplight-tb-scoped-styles';

const PAPER_INK = '#2a2318';
const LAMP = '#f0b054';
const RED = '#b8342a';
const BRASS = '#c99b52';
const BONE = '#e9dfcc';

function scopedCss(): string {
  return `
.lamptb { position: relative; min-height: 100%;
  /* the pool of lamplight (top, warm) over the wood desk — pool intensity = understanding */
  background:
    radial-gradient(62% 40% at 50% 0%, rgba(255,196,110,var(--pool1)), transparent 58%),
    radial-gradient(95% 60% at 50% 2%, rgba(255,176,80,var(--pool2)), transparent 70%),
    repeating-linear-gradient(88deg, rgba(58,42,24,.55) 0 3px, rgba(50,36,20,.3) 3px 8px),
    linear-gradient(135deg,#2c2013,#180f08);
  transition: background 1.6s cubic-bezier(.22,.61,.27,1);
}
.lamptb * { box-sizing: border-box; }
.lamptb::before { /* desk edge vignette */
  content:""; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  box-shadow: inset 0 0 220px 60px rgba(0,0,0,.72);
}

/* ===== the desk lamp — an articulated task lamp standing on the desk; its filament = understanding ===== */
.lamptb .lt-lamp { position: relative; width: 172px; height: 150px; margin: 0 auto -6px; pointer-events: none; }
.lamptb .lt-shade { position: absolute; top: 0; left: 50%; transform: translateX(-50%); }
.lamptb .lt-halo { position: absolute; top: 60px; left: 50%; width: 150px; height: 132px;
  transform: translateX(calc(-50% + 10px));
  border-radius: 50%; background: radial-gradient(circle, ${LAMP}, transparent 62%);
  transition: opacity 1.4s ease, transform 1.4s ease; }

/* ===== paper on the desk ===== */
.lamptb .lt-paper {
  background: linear-gradient(180deg, #f8f2e6, #f1e9d8); color: ${PAPER_INK};
  border-radius: 2px; padding: 26px 30px; position: relative;
  box-shadow: 0 34px 60px -16px rgba(0,0,0,.78), 0 3px 0 rgba(0,0,0,.35);
}
.lamptb .lt-paper.lt-tilt { transform: rotate(-1.1deg); }
.lamptb .lt-hdr { font-family: "IBM Plex Mono", monospace; font-size: 11.5px; color: #7a6f5b;
  border-bottom: 1px solid #d8ccb6; padding-bottom: 9px; margin-bottom: 14px; }
.lamptb .lt-hdr b { color: #3a3225; }
.lamptb .lt-line { display: block; margin: 0 0 10px; }
.lamptb .lt-body { display: inline; }
.lamptb .lt-frag { border-radius: 2px; padding: 0 1px; transition: background .2s; }
.lamptb .lt-frag.lt-live { cursor: pointer; }
.lamptb .lt-frag.lt-live:hover { background: rgba(184,52,42,.14); }
.lamptb .lt-frag.lt-struck { color: #8f2c20; position: relative;
  background-image: linear-gradient(${RED}, ${RED}); background-repeat: no-repeat;
  background-position: 0 60%; background-size: 100% 2px; animation: ltStrike .35s ease forwards; }
@keyframes ltStrike { from { background-size: 0 2px; } to { background-size: 100% 2px; } }
.lamptb .lt-frag.lt-residue { outline: 1.5px dashed ${RED}; outline-offset: 2px; }

/* the red felt pen resting on the desk */
.lamptb .lt-pen { position: absolute; right: -6px; bottom: -22px; width: 210px; transform: rotate(22deg);
  filter: drop-shadow(0 12px 12px rgba(0,0,0,.55)); z-index: 4; pointer-events: none; }

/* the coach's margin scrawl — a real pen note (adult, in context) */
.lamptb .lt-margin { font-family: "Caveat", cursive; font-weight: 600; color: ${RED};
  font-size: 22px; line-height: 1.15; }

/* coaching slips — index cards on the desk */
.lamptb .lt-slip { display: block; width: 100%; text-align: left; position: relative;
  background: linear-gradient(180deg, #f4ecda, #ece2cc); color: ${PAPER_INK};
  border: none; border-radius: 2px; padding: 11px 15px; margin-bottom: 9px; cursor: pointer;
  font-size: 13.5px; font-family: "Spectral", Georgia, serif; transform: rotate(var(--r,0deg));
  box-shadow: 0 10px 20px -8px rgba(0,0,0,.6); transition: transform .16s, box-shadow .16s; }
.lamptb .lt-slip:hover { transform: rotate(0) translateY(-2px); box-shadow: 0 16px 26px -8px rgba(0,0,0,.7); }
.lamptb .lt-slip.lt-placed { border-left: 4px solid ${RED}; padding-left: 26px; }
.lamptb .lt-slip .lt-ord { position: absolute; left: -10px; top: 50%; transform: translateY(-50%);
  width: 20px; height: 20px; border-radius: 50%; background: ${RED}; color: #fff;
  font-family: "IBM Plex Mono", monospace; font-size: 10px; display: grid; place-items: center;
  box-shadow: 0 3px 6px rgba(0,0,0,.5); }

.lamptb .lt-rise { animation: ltRise .7s cubic-bezier(.22,.61,.27,1) both; }
@keyframes ltRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.lamptb .lt-beat { transition: opacity .8s ease, filter .8s ease; }
.lamptb .lt-beat.lt-past { opacity: .55; filter: saturate(.8) brightness(.94); }

@media (prefers-reduced-motion: reduce) {
  .lamptb .lt-pool, .lamptb .lt-halo, .lamptb .lt-beat { transition: none !important; }
  .lamptb .lt-frag.lt-struck { animation: none !important; background-size: 100% 2px; }
  .lamptb .lt-rise { animation: none !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
  }, []);
}

// The lamp's warmth = the other person's understanding, by phase/outcome.
function warmthOf(phase: string, outcome: string | null): number {
  if (phase === 'done') {
    if (outcome === 'aha') return 1;
    if (outcome === 'unresolved' || outcome === 'backfire' || outcome === 'dependency') return 0.1;
    return 0.5;
  }
  if (phase === 'residue') return 0.62;
  if (phase === 'compose' || phase === 'reply' || phase === 'adapt') return 0.46;
  if (phase === 'diagnose') return 0.34;
  return 0.24;
}

// ── The lamp fixture — an articulated desk/task lamp standing on the desk.
// Weighted base → jointed arm → tilted shade throwing light down onto the paper.
// The filament in the shade mouth = the other person's understanding (warm).
function Lamp({ warm }: { warm: number }) {
  const filamentGlow = 0.35 + warm * 0.65;
  const coneOpacity = 0.06 + warm * 0.34;
  return (
    <div className="lt-lamp" aria-hidden>
      <div
        className="lt-halo"
        style={{ opacity: 0.1 + warm * 0.55, transform: `translateX(calc(-50% + 10px)) scale(${0.82 + warm * 0.4})`, filter: `blur(${10 + warm * 12}px)` }}
      />
      <svg className="lt-shade" width="172" height="150" viewBox="0 0 172 150" fill="none">
        <defs>
          <linearGradient id="lt-brass" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#e6c079" />
            <stop offset="0.5" stopColor="#b98a3f" />
            <stop offset="1" stopColor="#6f5228" />
          </linearGradient>
          <linearGradient id="lt-cone" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={LAMP} stopOpacity="0.9" />
            <stop offset="1" stopColor={LAMP} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* cone of light spilling from the shade mouth down onto the paper */}
        <path
          d="M80 92 L44 150 L150 150 L114 96 Z"
          fill="url(#lt-cone)"
          opacity={coneOpacity}
          style={{ transition: 'opacity 1.4s ease' }}
        />

        {/* ── the fixture ── */}
        {/* desk-contact shadow */}
        <ellipse cx="42" cy="145" rx="32" ry="6" fill="#000" opacity="0.5" />
        {/* weighted base */}
        <path d="M20 145 Q17 130 42 129 Q67 130 64 145 Z" fill="url(#lt-brass)" stroke="#5a4423" strokeWidth="1.2" />
        <ellipse cx="42" cy="130" rx="21" ry="4.5" fill="#d9b877" stroke="#5a4423" strokeWidth="0.8" />

        {/* lower arm */}
        <line x1="42" y1="128" x2="66" y2="80" stroke="url(#lt-brass)" strokeWidth="5.5" strokeLinecap="round" />
        {/* elbow joint (spring knuckle) */}
        <circle cx="66" cy="80" r="6" fill="#c99b52" stroke="#5a4423" strokeWidth="1.2" />
        <circle cx="66" cy="80" r="1.8" fill="#4a381c" />
        {/* upper arm */}
        <line x1="66" y1="80" x2="104" y2="52" stroke="url(#lt-brass)" strokeWidth="5.5" strokeLinecap="round" />
        {/* base knuckle */}
        <circle cx="42" cy="128" r="5" fill="#c99b52" stroke="#5a4423" strokeWidth="1.2" />

        {/* head joint where the shade pivots */}
        <circle cx="104" cy="52" r="6" fill="#c99b52" stroke="#5a4423" strokeWidth="1.2" />
        <circle cx="104" cy="52" r="1.8" fill="#4a381c" />

        {/* the tilted bell shade — mouth angled down toward the paper */}
        <path d="M98 46 Q105 42 113 51 L116 97 Q97 106 78 88 Z" fill="url(#lt-brass)" stroke="#5a4423" strokeWidth="1.3" />
        {/* inner-shade shadow rim */}
        <path d="M82 87 Q97 100 114 92" stroke="#4a381c" strokeWidth="1.4" fill="none" opacity="0.6" />

        {/* the glowing mouth — bulb + filament (the understanding) */}
        <ellipse cx="97" cy="92" rx="16" ry="8" fill="#fff6d8" opacity={0.3 + warm * 0.55}
          style={{ transition: 'opacity 1.2s ease' }} />
        <path
          d="M89 90 q4 8 8 4 q4 -8 8 0"
          stroke={LAMP}
          strokeWidth="2"
          fill="none"
          opacity={filamentGlow}
          style={{ filter: `drop-shadow(0 0 ${2 + warm * 9}px ${LAMP})`, transition: 'opacity 1.2s ease, filter 1.2s ease' }}
        />
        <ellipse cx="97" cy="92" rx="17.5" ry="8.5" fill="none" stroke={BRASS} strokeWidth="1.4" opacity="0.75" />
      </svg>
    </div>
  );
}

export function LamplightTeachBack({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: TeachBackSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  useScopedStyles();
  const m = useTeachBack(data, { onActivityResponse, onComplete, savedResponse });
  const warm = warmthOf(m.phase, m.outcome);

  const kicker: React.CSSProperties = {
    fontFamily: '"IBM Plex Mono", monospace',
    fontSize: 10.5,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    color: BRASS,
  };
  const italic: React.CSSProperties = { fontFamily: '"Spectral", Georgia, serif', fontStyle: 'italic', color: '#c9ba9a', fontSize: 14.5 };

  return (
    <div
      className="lamptb h-full w-full overflow-y-auto"
      style={{
        ['--pool1' as string]: String(0.1 + warm * 0.34),
        ['--pool2' as string]: String(0.02 + warm * 0.12),
        color: BONE,
        fontFamily: theme.fonts.body,
      }}
    >
      <div className="relative" style={{ zIndex: 3, maxWidth: 780, margin: '0 auto', padding: '40px 24px 96px' }}>
        <Lamp warm={warm} />
        {/* header — role, never "student" */}
        <div style={{ ...kicker, marginBottom: 10, textAlign: 'center' }}>By lamplight · coaching {data.student.role}</div>
        <h1
          style={{
            fontFamily: theme.fonts.heading,
            fontWeight: 600,
            fontSize: 'clamp(1.7rem, 4.4vw, 2.6rem)',
            lineHeight: 1.08,
            margin: 0,
            color: '#f7f0e1',
            textAlign: 'center',
            textShadow: `0 2px 30px rgba(240,176,84,${0.15 + warm * 0.25})`,
          }}
        >
          {data.title}
        </h1>
        {data.subtitle && <p style={{ color: '#b6a888', marginTop: 8, fontSize: 15, textAlign: 'center' }}>{data.subtitle}</p>}
        {data.instruction && <p style={{ ...italic, marginTop: 12, textAlign: 'center' }}>{data.instruction}</p>}

        {/* their voice */}
        <div style={{ marginTop: 22, borderLeft: `3px solid ${BRASS}`, paddingLeft: 14 }}>
          <div style={{ ...kicker, fontSize: 9.5, marginBottom: 3 }}>
            {data.student.name} · {data.student.role}
          </div>
          <p style={{ ...italic, fontSize: 16, margin: 0, color: '#ddd0b6' }}>“{data.student.voiceOpening}”</p>
        </div>

        {/* ===== BEAT 1 · RED PEN ===== */}
        <div className={`lt-beat${m.phase !== 'redpen' ? ' lt-past' : ''}`} style={{ marginTop: 30 }}>
          <div style={{ ...kicker, marginBottom: 10 }}>The red pen — strike what does damage</div>
          <div style={{ position: 'relative' }}>
            <Document
              className="lt-tilt"
              frags={data.artifact.fragments.map((f) => ({ id: f.id, text: f.text, strikeable: f.strikeable }))}
              header={data.artifact.header}
              struck={m.struck}
              locked={m.phase !== 'redpen'}
              onToggle={m.toggleStrike}
            />
            <svg className="lt-pen" viewBox="0 0 220 40" aria-hidden>
              <rect x="4" y="14" width="170" height="14" rx="7" fill={RED} />
              <rect x="4" y="14" width="52" height="14" rx="7" fill="#8f2820" />
              <rect x="170" y="16" width="26" height="10" rx="2" fill="#e6d6b8" />
              <path d="M196 16 l16 4 -16 4 Z" fill="#7a1f18" />
            </svg>
          </div>
          {m.phase === 'redpen' ? (
            <Btn disabled={m.struck.size === 0} onClick={m.penDown}>Pen down — that&apos;s what I&apos;d strike</Btn>
          ) : (
            <p className="lt-margin" style={{ marginTop: 14 }}>
              — you caught {m.damageStruck} of {m.damageTotal}
              {m.overStruck && '; and struck a line they needed — the pen cuts the good out too'}
            </p>
          )}
        </div>

        {/* ===== BEAT 2 · DIAGNOSE ===== */}
        {(m.phase === 'diagnose' || (m.phase !== 'redpen' && m.diagnosisId)) && (
          <div className={`lt-beat lt-rise${m.phase !== 'diagnose' && m.diagnosisId ? ' lt-past' : ''}`} style={{ marginTop: 28 }}>
            <div style={{ ...kicker, marginBottom: 6 }}>The belief underneath</div>
            <p style={{ ...italic, marginBottom: 12 }}>Not where it&apos;s wrong — what do they believe that made all of it feel right?</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.diagnosisOptions.map((o) => {
                const picked = m.diagnosisId === o.id;
                const locked = m.phase !== 'diagnose';
                return (
                  <button
                    key={o.id}
                    type="button"
                    disabled={locked}
                    onClick={() => m.chooseDiagnosis(o.id)}
                    style={{
                      textAlign: 'left',
                      background: picked ? `${(o.correct ? LAMP : BRASS)}26` : 'rgba(20,14,8,.55)',
                      border: `1px solid ${picked ? (o.correct ? LAMP : BRASS) : 'rgba(201,155,82,.3)'}`,
                      color: BONE,
                      borderRadius: 6,
                      padding: '12px 15px',
                      fontFamily: theme.fonts.body,
                      fontSize: 14,
                      cursor: locked ? 'default' : 'pointer',
                      opacity: locked && !picked ? 0.5 : 1,
                    }}
                  >
                    {o.text}
                  </button>
                );
              })}
            </div>
            {m.diagnosisId && m.chosenDiagnosis && <p className="lt-margin" style={{ marginTop: 12 }}>— {m.chosenDiagnosis.feedback}</p>}
          </div>
        )}

        {/* ===== BEAT 3 · COMPOSE ===== */}
        {(m.phase === 'compose' || m.phase === 'reply' || m.phase === 'adapt' || m.phase === 'residue' || m.phase === 'done') &&
          m.diagnosisId && (
            <div className={`lt-beat lt-rise${m.phase !== 'compose' ? ' lt-past' : ''}`} style={{ marginTop: 28 }}>
              <div style={{ ...kicker, marginBottom: 6 }}>Your coaching — in the order you&apos;d say it</div>
              <p style={{ ...italic, marginBottom: 12 }}>What you lead with changes how they hear the rest. {data.maxCoaching} slips.</p>
              {m.composed.map((id, i) => {
                const f = data.coachingFragments.find((x) => x.id === id);
                if (!f) return null;
                return (
                  <button key={id} type="button" className="lt-slip lt-placed" disabled={m.phase !== 'compose'} onClick={() => m.removeFragment(id)}>
                    <span className="lt-ord">{i + 1}</span>
                    {f.text}
                  </button>
                );
              })}
              {m.phase === 'compose' &&
                data.coachingFragments
                  .filter((f) => !m.composed.includes(f.id))
                  .map((f, i) => (
                    <button
                      key={f.id}
                      type="button"
                      className="lt-slip"
                      style={{ ['--r' as string]: `${(i % 2 === 0 ? -1 : 1) * 0.6}deg` }}
                      disabled={m.composed.length >= data.maxCoaching}
                      onClick={() => m.addFragment(f.id)}
                    >
                      {f.text}
                    </button>
                  ))}
              {m.phase === 'compose' && <Btn disabled={m.composed.length === 0} onClick={m.sayIt}>Say it to {data.student.name}</Btn>}
            </div>
          )}

        {/* reply */}
        {(m.phase === 'reply' || m.phase === 'adapt' || m.phase === 'residue' || m.phase === 'done') && m.studentReply && (
          <div className="lt-rise" style={{ marginTop: 16, borderLeft: `3px solid rgba(201,155,82,.35)`, paddingLeft: 14 }}>
            <div style={{ ...kicker, fontSize: 9.5, marginBottom: 3 }}>{data.student.name}</div>
            <p style={{ ...italic, fontSize: 15, margin: 0, color: '#ddd0b6' }}>{m.studentReply}</p>
            {m.coachNote && <p className="lt-margin" style={{ marginTop: 8, fontSize: 19 }}>— {m.coachNote}</p>}
          </div>
        )}

        {/* adapt */}
        {m.phase === 'adapt' && data.adaptBeat && (
          <div className="lt-rise" style={{ marginTop: 18 }}>
            <div style={{ ...kicker, marginBottom: 8 }}>{data.adaptBeat.prompt}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {data.adaptBeat.options.map((o) => (
                <button key={o.id} type="button" className="lt-slip" onClick={() => m.chooseAdapt(o.id)}>
                  {o.text}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ===== BEAT 5 · RESIDUE ===== */}
        {(m.phase === 'residue' || m.phase === 'done') && (
          <div className={`lt-beat lt-rise${m.phase === 'done' ? ' lt-past' : ''}`} style={{ marginTop: 28 }}>
            <div style={{ ...kicker, marginBottom: 6 }}>Their rewrite is back — final read</div>
            <p style={{ ...italic, marginBottom: 12 }}>It&apos;s better. But beliefs leave fingerprints. Approve it — or strike what shouldn&apos;t have survived.</p>
            <Document
              frags={data.revision.fragments.map((f) => ({ id: f.id, text: f.text, strikeable: f.strikeable, residue: f.isResidue && m.residueStruck }))}
              header={data.revision.header}
              struck={m.residueStruck ? new Set(data.revision.fragments.filter((f) => f.isResidue).map((f) => f.id)) : new Set<string>()}
              locked={m.phase !== 'residue'}
              onToggle={() => m.strikeResidue()}
            />
            {m.phase === 'residue' && (
              <Btn disabled={false} onClick={m.approveRevision}>{m.residueStruck ? 'Strike it & approve' : 'Approve — send it'}</Btn>
            )}
          </div>
        )}

        {/* ===== CLOSE ===== */}
        {m.phase === 'done' && (
          <div className="lt-rise" style={{ marginTop: 30 }}>
            <div style={{ borderLeft: `3px solid ${LAMP}`, paddingLeft: 14, marginBottom: 16 }}>
              <div style={{ ...kicker, fontSize: 9.5, marginBottom: 5 }}>The move a great mentor makes</div>
              <p style={{ ...italic, fontSize: 15, margin: 0, color: '#e4d8bf' }}>{data.expertNote}</p>
            </div>
            <ExperienceClose
              conclusion={data.conclusion}
              theme={theme}
              outcome={m.outcome ?? 'partial'}
              values={{
                damageFound: String(m.damageStruck),
                damageTotal: String(m.damageTotal),
                diagnosis: m.diagnosisCorrect ? 'named the belief' : 'read it as a surface slip',
              }}
              fallback="You coached a real belief today — that is the whole skill."
            />
            <div style={{ marginTop: 16, border: `1px solid rgba(201,155,82,.3)`, borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ ...kicker, fontSize: 9.5, padding: '8px 14px', background: 'rgba(20,14,8,.6)', borderBottom: '1px solid rgba(201,155,82,.3)', color: LAMP }}>
                Your record · what Kula keeps
              </div>
              <div style={{ padding: '12px 16px', fontFamily: '"IBM Plex Mono", monospace', fontSize: 12, color: '#b6a888', lineHeight: 1.9 }}>
                Red pen: <b style={{ color: BONE }}>{m.damageStruck}/{m.damageTotal}</b>
                {m.overStruck && ' · 1 needed line struck'} · Diagnosis:{' '}
                <b style={{ color: BONE }}>{m.diagnosisCorrect ? 'correct' : 'missed'}</b> · Outcome:{' '}
                <b style={{ color: BONE }}>{m.outcome}</b>
                {m.adapted && ' (adapted)'} · Residue:{' '}
                <b style={{ color: BONE }}>{m.residueCaught ? 'caught' : 'shipped'}</b>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// A sheet of paper on the desk — greeting/sign-off (non-strikeable framing) get
// their own line so it reads as a real email; body flows inline (email rhythm).
function Document({
  frags,
  header,
  struck,
  locked,
  onToggle,
  className,
}: {
  frags: Array<{ id: string; text: string; strikeable: boolean; residue?: boolean }>;
  header?: Array<{ label: string; value: string }>;
  struck: Set<string>;
  locked: boolean;
  onToggle: (id: string) => void;
  className?: string;
}) {
  return (
    <div className={`lt-paper${className ? ' ' + className : ''}`}>
      {header && header.length > 0 && (
        <div className="lt-hdr">
          {header.map((h, i) => (
            <span key={i} style={{ marginRight: 14 }}>
              {h.label}: <b>{h.value}</b>
            </span>
          ))}
        </div>
      )}
      <div style={{ fontFamily: '"Spectral", Georgia, serif', fontSize: 15, lineHeight: 1.75 }}>
        {frags.map((f) => {
          const isStruck = struck.has(f.id);
          const cls = `lt-frag${f.strikeable && !locked ? ' lt-live' : ''}${isStruck ? ' lt-struck' : ''}${f.residue ? ' lt-residue' : ''}`;
          if (!f.strikeable) return <span key={f.id} className="lt-line"><span className="lt-frag">{f.text}</span></span>;
          return (
            <span key={f.id} className="lt-body">
              <span
                role="button"
                tabIndex={locked ? -1 : 0}
                className={cls}
                onClick={() => !locked && onToggle(f.id)}
                onKeyDown={(e) => {
                  if (!locked && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onToggle(f.id);
                  }
                }}
              >
                {f.text}
              </span>{' '}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function Btn({ children, disabled, onClick }: { children: React.ReactNode; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        marginTop: 16,
        background: `linear-gradient(180deg, ${LAMP}, #d9982f)`,
        color: '#1a140d',
        border: 'none',
        borderRadius: 8,
        padding: '12px 26px',
        fontFamily: '"IBM Plex Mono", monospace',
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: '0.04em',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        boxShadow: disabled ? 'none' : '0 8px 22px rgba(240,176,84,.3)',
      }}
    >
      {children}
    </button>
  );
}
